import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import { ChevronRight, RefreshCw } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canViewReplacementOrders, canCreateReplacements } from "../../utils/roles";
import { fetchReplacementOrders, fetchReplacementOrdersSummary, retryReplacementWhatsApp } from "../../api/ReplacementAPIs";
import { fetchOrderById, updateOrderStatus, updateOrderStatusBulk } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { formatPhoneWithCaseStyle, teePlusPackedItemData } from "../../utils/orderLineDisplay";
import { copyOrderToTeePlus } from "../../utils/productionSource";

const STATUS_FILTERS = [
  "Replacement",
  "Girabatcom",
  "Printlet",
  "Printing Hold",
  "TeePlus",
  "Printed",
  "Hold",
  "Incomplete",
  "Old",
];

const STATUS_ACTIONS = STATUS_FILTERS.filter((status) => status !== "Replacement");

const ReplacementOrdersPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canView = canViewReplacementOrders(authState.role);
  const canAct = canCreateReplacements(authState.role);
  const [status, setStatus] = useState("Replacement");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["replacementOrders", status, page],
    queryFn: () => fetchReplacementOrders({ status, page, pageSize: 20 }),
    enabled: canView,
  });
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["replacementOrdersSummary"],
    queryFn: fetchReplacementOrdersSummary,
    enabled: canView,
  });

  useEffect(() => {
    setSelectedIds(new Set());
  }, [status, page]);

  if (!canView) return null;

  const items = data?.items || [];
  const total = data?.totalCount || 0;
  const visibleIds = items.map((order) => order.replacementOrderId);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const selectionEnabled = canAct && (status === "Replacement" || status === "Incomplete");

  const toggleSelected = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (visibleIds.every((id) => prev.has(id))) return new Set();
      return new Set(visibleIds);
    });
  };

  const selectedOrders = () => items.filter((order) => selectedIds.has(order.replacementOrderId));

  const refreshLists = () => {
    queryClient.invalidateQueries({ queryKey: ["replacementOrders"] });
    queryClient.invalidateQueries({ queryKey: ["replacementOrdersSummary"] });
  };

  const sendSelectedToStatus = async (nextStatus) => {
    const selected = selectedOrders();
    if (selected.length === 0) {
      toast.info("اختَر طلبات الأول");
      return;
    }
    if (!window.confirm(`نقل ${selected.length} طلب إلى ${nextStatus}؟`)) return;
    setBulkBusy(true);
    try {
      await updateOrderStatusBulk(selected.map((order) => order.replacementOrderId), nextStatus);
      toast.success(`تم نقل ${selected.length} طلب إلى ${nextStatus}`);
      setSelectedIds(new Set());
      refreshLists();
    } catch (err) {
      toast.error(err.message || "فشل النقل");
    } finally {
      setBulkBusy(false);
    }
  };

  const copySelectedToTeePlus = async () => {
    const selected = selectedOrders();
    if (selected.length === 0) {
      toast.info("اختَر طلبات الأول");
      return;
    }
    if (!window.confirm(`نسخ ${selected.length} طلب إلى TeePlus؟`)) return;
    setBulkBusy(true);
    try {
      let currentRef = localStorage.getItem("orderReference") || "Girabatcom.1";
      const lines = [];
      for (const row of selected) {
        const order = await fetchOrderById(row.replacementOrderId);
        const details = order.orderDetails || order.OrderDetails || [];
        const itemsForCopy = [];
        for (const detail of details) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;
          const baseItem = {
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: String(design.code || "").startsWith("CUSTOM")
              ? `${API_BASE_URL_V2}${design.driveLink}`
              : design.driveLink || "",
            code: design.code || "",
          };
          const quantity = Math.max(detail.quantity || 0, 1);
          for (let i = 0; i < quantity; i += 1) itemsForCopy.push(baseItem);
        }
        const phone2 = order.phoneNumber2;
        const phone = `${order.phoneNumber || ""}${phone2 && phone2 !== "Unknown Phone" && phone2 !== "غير معروف" ? `-${phone2}` : ""}`;
        const state = order.address?.state || order.address?.State || "";
        const address = order.address?.fullAddress || order.address?.FullAddress || "";
        const refForOrder = currentRef;
        const customerBase = `${refForOrder}\t\t${order.name || ""}\t${phone}\t\t${address}\t${state} (D)\t${order.totalAmount ?? 0}\tCOD\t`;
        itemsForCopy.forEach((item, index) => {
          const itemLine = `${index + 1}-${itemsForCopy.length}${teePlusPackedItemData(item)}`;
          lines.push(index === 0 ? `${customerBase}\t\t${itemLine}` : `${refForOrder}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        });
        const match = currentRef.match(/(\d+)$/);
        if (match) currentRef = currentRef.replace(/\d+$/, String(parseInt(match[0], 10) + 1));
      }
      localStorage.setItem("orderReference", currentRef);
      await copyOrderToTeePlus(lines.join("\n"), selected.map((order) => order.replacementOrderId));
      toast.success(`تم نسخ ${selected.length} طلب إلى TeePlus`);
      setSelectedIds(new Set());
    } catch (err) {
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error(err.message || "فشل النسخ");
    } finally {
      setBulkBusy(false);
    }
  };

  const changeStatus = async (orderId, nextStatus) => {
    let reason = null;
    if (nextStatus === "Hold") {
      reason = window.prompt("سبب التعليق:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("سبب التعليق مطلوب");
        return;
      }
    }
    if (!window.confirm(`نقل الطلب #${orderId} إلى ${nextStatus}؟`)) return;

    setBusyId(orderId);
    try {
      await updateOrderStatus(orderId, nextStatus, reason);
      toast.success(`الطلب #${orderId} بقى ${nextStatus}`);
      queryClient.invalidateQueries({ queryKey: ["replacementOrders"] });
      queryClient.invalidateQueries({ queryKey: ["replacementOrdersSummary"] });
    } catch (err) {
      toast.error(err.message || "فشل تحديث الحالة");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate("/dashboard")} className="p-2 rounded-xl bg-white shadow">
              <ChevronRight size={22} />
            </button>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800">طلبات الاستبدال</h1>
              <p className="text-sm text-gray-500">طلبات الاستبدال</p>
            </div>
          </div>
          <button type="button" onClick={() => refetch()} className="px-4 py-2 rounded-xl bg-white border flex items-center gap-2">
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} /> تحديث
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => { setStatus(item); setPage(0); }}
              className={`px-3 py-1.5 rounded-full text-sm font-bold ${status === item ? "bg-indigo-600 text-white" : "bg-white text-gray-700"}`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 bg-white rounded-2xl shadow p-4">
            <p className="text-sm text-gray-500">{status}</p>
            <p className="text-3xl font-extrabold text-indigo-700">{isLoading ? "..." : total}</p>
          </div>
          <div className="col-span-1 bg-white rounded-2xl shadow p-4">
            <p className="text-sm text-gray-500">كل طلبات الاستبدال</p>
            <p className="text-3xl font-extrabold text-gray-800">
              {summaryLoading ? "..." : Number(summary?.totalCount ?? summary?.TotalCount ?? 0)}
            </p>
          </div>
        </div>

        {selectionEnabled && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleSelectAll}
              disabled={items.length === 0 || bulkBusy}
              className="px-4 py-2 rounded-xl bg-white border font-bold text-sm disabled:opacity-40"
            >
              {allVisibleSelected ? "إلغاء التحديد" : "تحديد الكل"}
            </button>
            <span className="text-sm text-gray-500">{selectedIds.size} محدد</span>
            {status === "Replacement" && (
              <button
                type="button"
                onClick={() => sendSelectedToStatus("Printing Hold")}
                disabled={selectedIds.size === 0 || bulkBusy}
                className="px-4 py-2 rounded-xl bg-slate-800 text-white text-sm font-bold disabled:opacity-40"
              >
                إرسال المحدد إلى Printing Hold
              </button>
            )}
            {status === "Incomplete" && (
              <>
                <button
                  type="button"
                  onClick={copySelectedToTeePlus}
                  disabled={selectedIds.size === 0 || bulkBusy}
                  className="px-4 py-2 rounded-xl bg-emerald-700 text-white text-sm font-bold disabled:opacity-40"
                >
                  نسخ المحدد لـ TeePlus
                </button>
                <button
                  type="button"
                  onClick={() => sendSelectedToStatus("Old")}
                  disabled={selectedIds.size === 0 || bulkBusy}
                  className="px-4 py-2 rounded-xl bg-gray-700 text-white text-sm font-bold disabled:opacity-40"
                >
                  إرسال المحدد إلى Old
                </button>
              </>
            )}
          </div>
        )}

        {isLoading ? <p className="text-gray-500">جاري التحميل...</p> : (
          <div className="space-y-3">
            {items.length === 0 && <div className="bg-white rounded-2xl p-6 text-gray-500">لا توجد طلبات استبدال.</div>}
            {items.map((order) => (
              <article key={order.id || order.replacementOrderId} className="bg-white rounded-2xl shadow border border-white p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  {selectionEnabled && (
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5"
                      checked={selectedIds.has(order.replacementOrderId)}
                      onChange={() => toggleSelected(order.replacementOrderId)}
                      disabled={bulkBusy}
                    />
                  )}
                  <div className="flex-1">
                    <h2 className="font-bold text-lg">#{order.replacementOrderId}</h2>
                    <p className="text-sm text-gray-600">{order.name} · {order.phoneNumber}</p>
                    <p className="text-sm text-gray-600">{order.governorate} {order.city ? `· ${order.city}` : ""}</p>
                  </div>
                  <div className="text-left">
                    <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-bold">{order.status}</span>
                    <p className="text-sm mt-1">الإجمالي {order.totalAmount}</p>
                  </div>
                </div>
                <p className="text-sm">
                  أصل #{order.originalOrderId}
                  {order.ticketId ? ` · تذكرة #${order.ticketId}` : " · بدون تذكرة"}
                  {order.problemReason ? ` · ${order.problemReason}` : ""}
                  · كمية {order.quantity} · بنود {order.lineCount}
                </p>
                {order.resolveWhatsAppStatus && (
                  <p className="text-sm text-gray-600">
                    واتساب الحل: {order.resolveWhatsAppStatus}
                    {canAct && order.resolveWhatsAppStatus === "Failed" && (
                      <button
                        type="button"
                        disabled={busyId === order.replacementOrderId}
                        onClick={async () => {
                          setBusyId(order.replacementOrderId);
                          try {
                            const result = await retryReplacementWhatsApp(order.replacementOrderId);
                            toast.success(result.status === "Sent" ? `تم إرسال واتساب للطلب #${order.replacementOrderId}` : `واتساب: ${result.status || "غير معروف"}`);
                            queryClient.invalidateQueries({ queryKey: ["replacementOrders"] });
                          } catch (err) {
                            toast.error(err.message || "فشل إعادة إرسال واتساب");
                          } finally {
                            setBusyId(null);
                          }
                        }}
                        className="mr-2 font-bold text-indigo-700 disabled:opacity-50"
                      >
                        إعادة الإرسال
                      </button>
                    )}
                  </p>
                )}
                <Link to={`/search-orders?orderId=${order.replacementOrderId}`} className="inline-block text-sm font-bold text-indigo-700">
                  فتح الطلب
                </Link>
                {canAct && (
                  <div className="flex flex-wrap gap-2">
                    {STATUS_ACTIONS.map((action) => (
                      <button
                        key={action}
                        type="button"
                        disabled={busyId === order.replacementOrderId || order.status === action}
                        onClick={() => changeStatus(order.replacementOrderId, action)}
                        className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs disabled:opacity-40"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <span>{total} طلب</span>
          <div className="flex gap-2">
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 py-1 rounded-lg bg-white disabled:opacity-40">السابق</button>
            <button type="button" disabled={(page + 1) * 20 >= total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded-lg bg-white disabled:opacity-40">التالي</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplacementOrdersPage;
